import { ActionCreator, AnyAction } from 'redux';
import { Event, EventId } from '../types';
import { DynamicIds, EventIds, RequestDataIds } from '../constants/ABA';
import {
  changeAmsSegments,
  changeNavigation,
  changePageCallback,
  changeParamLanguage,
  setParamAssetsManifest,
  setParamBaseURL,
  setParamBrand,
  setParamChannel,
  setParamClientId,
  setParamContainer,
  setParamDepositUrl,
  setParamLanguage,
  setParamLayoutDimensions,
  setParamPlatform,
  setParamProxyPrefix,
  setParamUserData,
  setParamTheme,
  setParamCrossDomainClickStreamId,
  setParamUserToken,
  setParamWithdrawalUrl,
  userLogin,
  userLogout,
  changeUserBalance,
  updateNavbarCallback,
  setParamOperatorId,
  setParamBrandHash,
  setParamApplicationId,
  setParamAPIKey,
  changeLayoutDimensions,
} from '../actions';

export const eventToActionMap: { [key: string]: { actionCreator: ActionCreator<AnyAction> } } = {
  [RequestDataIds.RenderContainer]: {
    actionCreator: setParamContainer,
  },
  [RequestDataIds.Language]: {
    actionCreator: setParamLanguage,
  },
  [RequestDataIds.BaseUrl]: {
    actionCreator: setParamBaseURL,
  },
  [RequestDataIds.Theme]: {
    actionCreator: setParamTheme,
  },
  [RequestDataIds.CrossDomainClickStreamId]: {
    actionCreator: setParamCrossDomainClickStreamId,
  },
  [RequestDataIds.Operator]: {
    actionCreator: setParamOperatorId,
  },
  [RequestDataIds.BrandHash]: {
    actionCreator: setParamBrandHash,
  },
  [RequestDataIds.ApplicationId]: {
    actionCreator: setParamApplicationId,
  },
  [RequestDataIds.APIKey]: {
    actionCreator: setParamAPIKey,
  },
  [RequestDataIds.Brand]: {
    actionCreator: setParamBrand,
  },
  [RequestDataIds.Channel]: {
    actionCreator: setParamChannel,
  },
  [RequestDataIds.ProxyPrefix]: {
    actionCreator: setParamProxyPrefix,
  },
  [RequestDataIds.UserToken]: {
    actionCreator: setParamUserToken,
  },
  [RequestDataIds.UserData]: {
    actionCreator: setParamUserData,
  },
  [RequestDataIds.ClientId]: {
    actionCreator: setParamClientId,
  },
  [RequestDataIds.Platform]: {
    actionCreator: setParamPlatform,
  },
  [RequestDataIds.DepositUrl]: {
    actionCreator: setParamDepositUrl,
  },
  [RequestDataIds.WithdrawalUrl]: {
    actionCreator: setParamWithdrawalUrl,
  },
  [RequestDataIds.LayoutDimensions]: {
    actionCreator: setParamLayoutDimensions,
  },
  [RequestDataIds.AssetsManifest]: {
    actionCreator: setParamAssetsManifest,
  },
  [EventIds.ChangeLanguage]: {
    actionCreator: changeParamLanguage,
  },
  [EventIds.UserLogin]: {
    actionCreator: userLogin,
  },
  [EventIds.UserLogout]: {
    actionCreator: userLogout,
  },
  [EventIds.ChangeNavigation]: {
    actionCreator: changeNavigation,
  },
  [EventIds.AmsSegments]: {
    actionCreator: changeAmsSegments,
  },
  [EventIds.ChangeUserBalance]: {
    actionCreator: changeUserBalance,
  },
  [EventIds.UpdateNavbarCallback]: {
    actionCreator: updateNavbarCallback,
  },
  [EventIds.ChangeLayoutDimensions]: {
    actionCreator: changeLayoutDimensions,
  },
};

export const dynamicEventToActionMap = [
  {
    actionCreator: changePageCallback,
    regexp: new RegExp('^change:page:(.*):(.*)'),
  },
];

export const actionFromShellEvent = (event: Event): AnyAction => {
  const eventId = event.id;

  /**
   * Static keys
   */
  const staticEventToAction = eventToActionMap[eventId];

  if (staticEventToAction) {
    let actionPayload = getActionPayload(event);

    if (eventId === RequestDataIds.BaseUrl) {
      actionPayload = patchBaseURLActionPayload(actionPayload);
    }

    // TODO: need remove after refusing Air host
    // Сrutch for Air. It is necessary to bring the parameters into a single form
    // @ts-ignore
    if ([RequestDataIds.Brand, RequestDataIds.Channel].includes(eventId)) {
      actionPayload = actionPayload?.toUpperCase();
    }

    return staticEventToAction.actionCreator(actionPayload);
  }

  /**
   * Dynamic keys
   */
  const dynamicEventToAction = dynamicEventToActionMap.find((eventToAction) => eventToAction.regexp.test(eventId));

  if (dynamicEventToAction) {
    return dynamicEventToAction.actionCreator(
      getActionPayload(event),
      extractMetaInfo(dynamicEventToAction.regexp, eventId)
    );
  }

  return { type: DynamicIds.Unhandled, payload: event };
};

// Should handle location.pathname !== baseURL from host
export function patchBaseURLActionPayload(baseURL: string): string {
  const currentPathname = window.location.pathname;
  if (currentPathname === '/') return baseURL;

  /**
   * location.pathname = "/casino/live-casino"
   * baseURL = "/ru/casino/live-casino"
   * return = "/casino/live-casino"
   */
  const payloadWithoutLanguage = baseURL.replace(/^\/[a-z]{2}\//, '/');
  if (currentPathname.startsWith(payloadWithoutLanguage)) {
    return payloadWithoutLanguage;
  }

  /**
   * location.pathname = "/ru/casino/live-casino"
   * baseURL = "/casino/live-casino"
   * return = "/ru/casino/live-casino"
   */
  const langPrefixRegex = new RegExp(`^((?!(${baseURL})).)*`);
  const langPrefix = langPrefixRegex.exec(currentPathname)?.[0];
  if (langPrefix) {
    return `${langPrefix}${baseURL}`;
  }

  return baseURL;
}

// Get payload from event or return default payload
export function getActionPayload(event: Event): string {
  return event.payload
    ? event.payload.hasOwnProperty('data')
      ? // @ts-ignore
        event.payload.data
      : event.payload
    : undefined;
}

// Build meta data for dynamic host events
export function extractMetaInfo(regexp?: RegExp | null, id?: EventId | null) {
  if (!regexp || !regexp.exec || !id) {
    return {};
  }

  const result = regexp.exec(id);

  if (!result) {
    return {};
  }

  const [, page, type] = result;

  return {
    page,
    type,
    regexp,
  };
}
